import React from 'react'
import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faUpload,
  faTrashAlt,
  faCirclePlus,
  faDownload,
  faSpinner,
  faArrowRotateLeft,
} from '@fortawesome/free-solid-svg-icons'
import SideBar from '../../SideBar'
import moment from 'moment';
import useAxiosPrivateSAI from '../../../hooks/useAxiosPrivateSAI'
import useAuth from '../../../hooks/useAuth'
import { usesCoverAndCare } from '../../../config/schoolOverrides'

const Holidays = () => {
  const axios = useAxiosPrivateSAI()
  const { createInfo } = useAuth()
  const [policyData, setPolicyData] = useState(null)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = moment(dateStr, moment.ISO_8601, true);
    return date.isValid() ? date.format('YYYY-MM-DD') : '';
  };
  const [holidays, setHolidays] = useState({});
  const [activeView, setActiveView] = useState('form')
  const [isDraft, setIsDraft] = useState(false);
  const [insetDays, setInsetDays] = useState([])
  const [orgType, setOrgType] = useState(null)
  const isCoverAndCareSchool = usesCoverAndCare(orgType?.id)
  const insetLabel = isCoverAndCareSchool ? 'Development Day' : 'Inset Day'
  const [schools, setSchools] = useState([])
  const [isFetchingSchools, setIsFetchingSchools] = useState(false)
  const getAcademicYears = () => {
    const currentMonth = moment().month()
    const currentYear = moment().year()
    const startYear = currentMonth >= 8 ? currentYear : currentYear - 1

    return [
      `${startYear - 1}-${startYear}`,
      `${startYear}-${startYear + 1}`,
      `${startYear + 1}-${startYear + 2}`,
    ]
  }
  const academicYears = getAcademicYears()
  const [selectedYear, setSelectedYear] = useState(academicYears[1])
  const [isFetchingHolidays, setIsFetchingHolidays] = useState(false)
  const [isSubmittingHolidays, setIsSubmittingHolidays] = useState(false)
  const [isAutofillingLA, setIsAutofillingLA] = useState(false)
  const [insetRemovingById, setInsetRemovingById] = useState({})
  const [holidayErrors, setHolidayErrors] = useState({})
  const [insetErrors, setInsetErrors] = useState({})
  const [insetDirty, setInsetDirty] = useState({})
  const hasUnsavedInsets = insetDays.some(inset => insetDirty[inset.id])
  const [isUpdatingInset, setIsUpdatingInset] = useState(false)
  const hasInsetErrors = insetDays.some(inset => Boolean(insetErrors[inset.id]))
  const [isUpdatingHoliday, setIsUpdatingHoliday] = useState(false)
  const hasHolidayErrors = Object.values(holidayErrors).some(Boolean)

  const holidayLabels = isCoverAndCareSchool ? [
    { label: 'Autumn Holiday', key: 'autumn' },
    { label: 'Spring Holiday', key: 'spring' },
    { label: 'Summer Holiday', key: 'summer' },
    { label: 'Winter Holiday', key: 'xmas' },
  ] : [
    { label: 'Autumn Half Term', key: 'autumn' },
    { label: 'Christmas Holiday', key: 'xmas' },
    { label: 'Spring Half Term', key: 'spring' },
    { label: 'Easter Holiday', key: 'easter' },
    { label: 'Summer Half Term', key: 'summerHalf' },
    { label: 'Summer Holiday', key: 'summer' },
  ]

  const fetchHolidays = async (orgType, academyId, year) => {
    setIsFetchingHolidays(true)
    try {
      const response = await axios.get(
        `/api/v1/staff-hub/holidays/ajax_get/${orgType}/${academyId}/${year}?api_token=${process.env.REACT_APP_KIS_TOKEN}`
      );
      const data = response.data;

      setHolidays({
        autumn: {
          start: formatDate(data.autumn?.start_date),
          end: formatDate(data.autumn?.end_date),
        },
        xmas: {
          start: formatDate(data.xmas?.start_date),
          end: formatDate(data.xmas?.end_date),
        },
        spring: {
          start: formatDate(data.spring?.start_date),
          end: formatDate(data.spring?.end_date),
        },
        easter: {
          start: formatDate(data.easter?.start_date),
          end: formatDate(data.easter?.end_date),
        },
        summerHalf: {
          start: formatDate(data.summerHalf?.start_date),
          end: formatDate(data.summerHalf?.end_date),
        },
        summer: {
          start: formatDate(data.summer?.start_date),
          end: formatDate(data.summer?.end_date),
        },
      });

      setInsetDays(data.inset || []);
      setInsetErrors({});
      setInsetDirty({});
      setHolidayErrors({});
    } catch (error) {
      console.error('Error fetching holidays:', error);
    } finally {
      setIsFetchingHolidays(false)
    }
  }

  useEffect(() => {
    const storedPolicy = JSON.parse(localStorage.getItem('policyData'))
    const storedOrg = JSON.parse(localStorage.getItem('orgType'))
    setPolicyData(storedPolicy)
    setOrgType(storedOrg)
  }, [])

  useEffect(() => {
    if (!policyData?.policy_id || !orgType?.type || !orgType?.id) return
    fetchHolidays(orgType.type, orgType.id, selectedYear)
  }, [selectedYear, policyData, orgType])

  const fetchSchools = async (type, id) => {
    setIsFetchingSchools(true)
    setSchools([])
    try {
      const { data } = await axios.get(
        `/api/v1/staff-hub/policys/org_policys/${type}/${id}?area=holidays&api_token=${process.env.REACT_APP_KIS_TOKEN}`
      )
      if (!Array.isArray(data)) {
        throw new Error('Expected an array of schools')
      }
      setSchools(data)
    } catch (error) {
      createInfo('error', 'Failed to load schools for holiday reset.')
    } finally {
      setIsFetchingSchools(false)
    }
  }

  useEffect(() => {
    if (!orgType?.type || !orgType?.id) return
    fetchSchools(orgType.type, orgType.id)
  }, [orgType])

  const addInsetDay = async () => {
    try {
      const response = await axios.get(
        `/api/v1/staff-hub/holidays/inset_create/${orgType.type}/${orgType.id}/${selectedYear}?api_token=${process.env.REACT_APP_KIS_TOKEN}`
      );

      setInsetDays(prev => [
        ...prev,
        {
          id: response.data,
          description: isCoverAndCareSchool ? 'Inset Day' : '',
          start_date: '',
          end_date: '',
        },
      ]);
    } catch (error) {
      createInfo('error', `Failed to create ${insetLabel}`);
    }
  };

  const handleHolidayChange = async (key, field, value) => {
    const updated = {
      ...holidays,
      [key]: {
        ...holidays[key],
        [field]: value,
      },
    };

    setHolidays(updated);
    const { start, end } = updated[key];
    const showError = (message) => {
      setHolidayErrors(prev => ({ ...prev, [key]: message }));
      createInfo('error', message);
    };

    setIsUpdatingHoliday(true);
    try {
      const { data } = await axios.post(
        `/api/v1/staff-hub/holidays/ajax_update/${orgType.type}/${orgType.id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`,
        {
          start,
          end,
          tagName: key,
          year: selectedYear,
          holiday_status: 'draft',
        }
      );
      if (data?.error === true) {
        showError(data.message || 'Failed to update holiday');
        return;
      }
      setHolidayErrors(prev => ({ ...prev, [key]: '' }));
      setIsDraft(true);
    } catch (error) {
      const data = error.response?.data;
      showError(data?.message || 'Failed to update holiday. Please try again.');
    } finally {
      setIsUpdatingHoliday(false);
    }
  }


  const handleInsetChange = (idx, field, value) => {
    const updated = [...insetDays];
    updated[idx] = { ...updated[idx], [field]: value };
    if (isCoverAndCareSchool && field === 'start_date') {
      const previous = insetDays[idx];
      const duration = moment(previous.end_date || previous.start_date).diff(moment(previous.start_date), 'days');
      updated[idx].end_date = value
        ? moment(value).add(Number.isFinite(duration) && duration >= 0 && duration <= 2 ? duration : 0, 'days').format('YYYY-MM-DD')
        : '';
    }
    setInsetDays(updated);
    setInsetDirty(prev => ({ ...prev, [updated[idx].id]: true }));
  }

  const saveInset = async (idx) => {
    const inset = insetDays[idx];

    const showError = (message) => {
      setInsetErrors(prev => ({ ...prev, [inset.id]: message }));
      // createInfo('error', message);
    };

    if (!moment(inset.start_date || '', 'YYYY-MM-DD', true).isValid()) {
      showError('Select a valid start date before saving.');
      return;
    }

    if (isCoverAndCareSchool) {
      const start = moment(inset.start_date, 'YYYY-MM-DD', true);
      const end = moment(inset.end_date || inset.start_date, 'YYYY-MM-DD', true);
      const days = end.diff(start, 'days') + 1;
      if (!start.isValid() || !end.isValid() || days < 1 || days > 3) {
        showError('Development must last between 1 and 3 calendar days, including the start and end dates.');
        return;
      }
    }

    try {
      const response = await axios.post(`/api/v1/staff-hub/holidays/ajax_update/${orgType.type}/${orgType.id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        id: inset.id || undefined,
        start: inset.start_date,
        ...(isCoverAndCareSchool ? { end: inset.end_date || inset.start_date } : {}),
        description: inset.description,
        tagName: 'inset',
        year: selectedYear,
        holiday_status: 'draft',
      })
      if (response.data?.error === true) {
        showError(response.data.message || `Failed to update ${insetLabel}`);
        return;
      }
      setInsetErrors(prev => ({ ...prev, [inset.id]: '' }));
      setInsetDirty(prev => ({ ...prev, [inset.id]: false }));
      setIsDraft(true);

    } catch (error) {
      const data = error.response?.data;
      if (data?.error === true) {
        showError(data.message || `Failed to update ${insetLabel}`);
      } else {
        showError(`Failed to update ${insetLabel}. Please try again.`);
      }
    }
  }

  const saveInsets = async () => {
    if (isUpdatingInset) return;
    setIsUpdatingInset(true);
    try {
      for (let idx = 0; idx < insetDays.length; idx += 1) {
        if (insetDirty[insetDays[idx].id]) await saveInset(idx);
      }
    } finally {
      setIsUpdatingInset(false);
    }
  }

  const submitHolidays = async () => {
    if (hasHolidayErrors || hasInsetErrors || hasUnsavedInsets || isUpdatingHoliday || isUpdatingInset) return;
    setIsSubmittingHolidays(true)
    try {
      if (!orgType?.id) {
        console.warn("Organisation ID not found.");
        return;
      }

      const response = await axios.post(`/api/v1/staff-hub/holidays/submit?api_token=${process.env.REACT_APP_KIS_TOKEN}`, {
        ids: [orgType.id]
      });

      if (response.data?.error === false) {
        await fetchHolidays(orgType.type, orgType.id, selectedYear);
        setIsDraft(false)
        createInfo('success', 'Holidays submitted successfully.')
      } else {
        setIsDraft(true)
        createInfo('error', response.data?.message || 'Something went wrong.')
      }
    } catch (error) {
      setIsDraft(true)
      createInfo('error', 'Failed to submit holidays')
    } finally {
      setIsSubmittingHolidays(false)
    }
  }

  const removeInset = async (id) => {
    setInsetRemovingById(prev => ({ ...prev, [id]: true }))
    try {
      const response = await axios.post(
        `/api/v1/staff-hub/holidays/inset/${id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`)

      if (response.data?.error === true) {
        createInfo('error', response.data.message || `Failed to delete ${insetLabel}`);
        return;
      }
      setInsetDays(prev => prev.filter(inset => inset.id !== id));
      setInsetErrors(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      createInfo('success', `${insetLabel} removed`)
      return response.data;
    } catch (error) {
      createInfo('error', `Failed to delete ${insetLabel}`)
      throw error;
    } finally {
      setInsetRemovingById(prev => ({ ...prev, [id]: false }))
    }
  }

  const resetHolidayStore = () => {
    const ids = schools.map((school) => school.organization_id).filter(Boolean);
    if (isFetchingSchools || ids.length === 0) {
      createInfo('error', 'Schools must be loaded before resetting holidays.');
      return;
    }

    axios
      .post(`/api/v1/staff-hub/holidays/reset?api_token=${process.env.REACT_APP_KIS_TOKEN}`, { ids })
      .then((response) => {
        if (response.data === 'draft') {
        } else if (response.data === 'error') {
          setTimeout(() => {
            createInfo('error', 'You have not committed any staff details yet')
          }, 500);
        } else if (!response.data.error) {
          window.location.reload()
        }

        setIsDraft(true);
      })
      .catch((error) => {
        console.error('Error resetting holiday store:', error);
      });
  }

  const mapHolidayNameToKey = (rawName = '') => {
    const name = rawName.toLowerCase().trim()
    if (isCoverAndCareSchool) {
      return {
        'autumn holiday': 'autumn',
        'spring holiday': 'spring',
        'summer holiday': 'summer',
        'winter holiday': 'xmas',
      }[name] || null
    }

    // Half-terms
    if (name.includes('autumn') && name.includes('half')) return 'autumn'
    if (name.includes('spring') && name.includes('half')) return 'spring'
    if (name.includes('summer') && name.includes('half')) return 'summerHalf'

    // Full holidays
    if (name.includes('summer') && name.includes('holiday')) return 'summer'
    if (name.includes('spring') && name.includes('holiday')) return 'easter'
    if (name.includes('autumn') || name.includes('autumn')) return 'xmas'

    if (name === 'autumn holiday') return 'xmas'

    return null
  }

  const parseLARecordsToState = (records = [], year) => {
    const rows = records.filter(row => (row.academic_year || '').trim() === (year || '').trim())

    const next = {
      autumn: { start: '', end: '' },
      xmas: { start: '', end: '' },
      spring: { start: '', end: '' },
      easter: { start: '', end: '' },
      summerHalf: { start: '', end: '' },
      summer: { start: '', end: '' },
    }

    rows.forEach(row => {
      const key = mapHolidayNameToKey(row.holiday)
      if (!key) return
      next[key] = {
        start: formatDate(row.start_date),
        end: formatDate(row.end_date),
      }
    })

    return next
  }

  const applyLAHolidays = async (laRows) => {
    const next = parseLARecordsToState(laRows, selectedYear);
    setHolidays(next)
    setInsetDays([])
    setIsDraft(true)
  }

  const autofillLAHolidays = async () => {
    setIsAutofillingLA(true)
    try {
      const orgType = JSON.parse(localStorage.getItem('orgType'))

      if (!orgType?.type || !orgType?.id) {
        createInfo('error', 'Missing organisation details.')
        return;
      }
      const { data } = await axios.get(
        `/api/v1/staff-hub/holidays/get-la-holidays/${orgType.type}/${orgType.id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`
      )


      const laHoliday = data?.data || [];
      if (!laHoliday || (Array.isArray(laHoliday) && laHoliday.length === 0)) {
        createInfo('error', 'No LA Holidays found for this borough.')
        return;
      }

      const filtered = laHoliday.filter(r => (r.academic_year || '').trim() === (selectedYear || '').trim())
      if (filtered.length === 0) {
        createInfo('error', `No LA holidays found for ${selectedYear}.`)
        return
      }

      await applyLAHolidays(laHoliday)
      createInfo('success', 'LA holidays applied.')

    } catch (error) {
      console.error('Could not fetch LA Holidays', error);
      createInfo('error', 'Failed to fetch LA Holidays. Please try again later.')
    } finally {
      setIsAutofillingLA(false);
    }
  }

  const isHolidayDataLoading = isFetchingHolidays || isSubmittingHolidays || isAutofillingLA || isUpdatingHoliday || isUpdatingInset

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row max-w-[90rem] mx-auto lg:space-x-6">
        <main className="w-full">
          <div className="flex items-center gap-4">
            {isDraft ? (
            <div className='flex justify-start'>
              <div className="bg-orange-100 border border-orange-300 text-orange-800 px-4 py-2 rounded shadow text-sm my-4">
                Holidays have unsaved changes.
              </div>
            </div>
          ) : (
            null
          )}

           
            {/* <button onClick={autofillLAHolidays}
              disabled={isHolidayDataLoading}
              className="inline-flex items-center border border-white text-sas bg-white rounded-md overflow-hidden text-sm font-medium shadow">
              <span className="px-4 py-2 inline-flex items-center gap-2">
                {isAutofillingLA ? (
                  <>
                    Loading...
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </>
                ) : (
                  'Autofull LA Holidays'
                )}
              </span>
              <span className="bg-sas text-white px-3 py-2">
                <FontAwesomeIcon icon={faDownload} />
              </span>
            </button> */}
          </div>
          {/* {isFetchingHolidays && (
            <div className="flex justify-end items-center gap-2 text-sm text-sas mt-2">
              <span>Loading...</span>
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          )} */}

          {/* Tabs */}
          <div className="flex space-x-2 mt-4">
            {academicYears.map((year) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                disabled={isHolidayDataLoading}
                className={`px-4 py-2 rounded-t-md font-medium shadow-md border ${year === selectedYear
                  ? 'bg-sas text-white border-sas'
                  : 'bg-white text-gray-700 border-white'
                  }`}
              >
                {year}
              </button>
            ))}
          </div>

          {/* Holiday Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-0">
            {/* Left Column */}
            <div className="bg-white rounded-b-lg rounded-r-lg shadow-md p-6">
              <h4 className="font-semibold text-sas-dark mb-2 border-b pb-2">Holidays</h4>
              {holidayLabels.map(({ label, key }) => (
                <div key={key} className="mb-4">
                  <label className="block text-sm font-bold text-gray-700 mb-1">{label}</label>
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="date"
                      value={holidays[key]?.start || ''}
                      onChange={(e) => handleHolidayChange(key, 'start', e.target.value)}
                      disabled={isHolidayDataLoading}
                      aria-label={`${label} start date`}
                      aria-invalid={Boolean(holidayErrors[key])}
                      aria-describedby={holidayErrors[key] ? `${key}-error` : undefined}
                      className={`border ${holidayErrors[key] ? 'border-red-500' : 'border-sas'} rounded-md px-2 py-1 text-sm`}
                    />
                    <input
                      type="date"
                      value={holidays[key]?.end || ''}
                      onChange={(e) => handleHolidayChange(key, 'end', e.target.value)}
                      disabled={isHolidayDataLoading}
                      aria-label={`${label} end date`}
                      aria-invalid={Boolean(holidayErrors[key])}
                      aria-describedby={holidayErrors[key] ? `${key}-error` : undefined}
                      className={`border ${holidayErrors[key] ? 'border-red-500' : 'border-sas'} rounded-md px-2 py-1 text-sm`}
                    />
                  </div>
                  {holidayErrors[key] && (
                    <p id={`${key}-error`} role="alert" className="mt-1 text-sm text-red-600">
                      {holidayErrors[key]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Right Column */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center  mb-2 border-b pb-2">
                <h4 className="font-semibold text-sas-dark">{isCoverAndCareSchool ? 'Development Days' : 'Additional Days'}</h4>
                <button type="button"
                  onClick={addInsetDay}
                  disabled={isHolidayDataLoading}
                  className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden text-sm font-medium">
                  <span className="px-4 py-2">Add {insetLabel}</span>
                  <span className="bg-sas text-white px-3 py-2">
                    <FontAwesomeIcon icon={faPlus} />
                  </span>
                </button>
              </div>
              {insetDays.map((inset, idx) => (
                <div key={inset.id} className={isCoverAndCareSchool ? 'mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4' : 'mb-3'}>
                  <div className={isCoverAndCareSchool ? 'grid grid-cols-2 gap-3' : 'flex items-center gap-2'}>
                  <select
                    value={inset.description}
                    onChange={(e) =>
                      handleInsetChange(idx, 'description', e.target.value)
                    }
                    disabled={isHolidayDataLoading || insetRemovingById[inset.id] === true}
                    aria-label="Additional day type"
                    aria-invalid={Boolean(insetErrors[inset.id])}
                    aria-describedby={insetErrors[inset.id] ? `inset-${inset.id}-error` : undefined}
                    className={`${isCoverAndCareSchool ? 'order-1 w-full min-w-0' : 'w-1/2'} border ${insetErrors[inset.id] ? 'border-red-500' : 'border-sas'} px-2 py-1 text-sm rounded h-8`}
                  >
                    <option value="">Select Type</option>
                    <option value="Inset Day">{insetLabel}</option>
                    <option value="Religious Holiday">Religious Holiday</option>
                    <option value="Weather Closure">Weather Closure</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className={isCoverAndCareSchool ? 'order-3 col-span-2 sm:col-span-1 min-w-0' : 'w-1/2'}>
                  {isCoverAndCareSchool && (
                    <label htmlFor={`inset-${inset.id}-start`} className="mb-1 block text-sm text-gray-700">
                      Start date
                    </label>
                  )}
                  <input
                    id={`inset-${inset.id}-start`}
                    type="date"
                    value={formatDate(inset.start_date)}
                    onChange={(e) =>
                      handleInsetChange(idx, 'start_date', e.target.value)
                    }
                    disabled={isHolidayDataLoading || insetRemovingById[inset.id] === true}
                    aria-label={isCoverAndCareSchool ? 'Development start date' : 'Additional day date'}
                    aria-invalid={Boolean(insetErrors[inset.id])}
                    aria-describedby={insetErrors[inset.id] ? `inset-${inset.id}-error` : undefined}
                    className={`w-full min-w-0 border px-2 py-1 text-sm rounded h-9 ${insetErrors[inset.id] ? 'border-red-500' : 'border-sas'}`}
                  />
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${insetLabel}`}
                    onClick={() => removeInset(inset.id)}
                    disabled={isHolidayDataLoading || insetRemovingById[inset.id] === true}
                    className={`${isCoverAndCareSchool ? 'order-2 justify-self-end self-center p-2' : ''} text-red-500 disabled:text-gray-400 disabled:cursor-not-allowed`}
                  >
                    {insetRemovingById[inset.id] === true ? (
                      <span className="inline-flex items-center gap-2 text-xs">
                        Loading...
                        <FontAwesomeIcon icon={faSpinner} spin />
                      </span>
                    ) : (
                      <FontAwesomeIcon icon={faTrashAlt} className="cursor-pointer" />
                    )}
                  </button>
                  {isCoverAndCareSchool && (
                    <div className="order-4 col-span-2 sm:col-span-1 min-w-0">
                      <label className="block text-sm text-gray-700">
                        End date
                        <input
                          type="date"
                          value={formatDate(inset.end_date || inset.start_date)}
                          min={formatDate(inset.start_date) || undefined}
                          max={inset.start_date ? moment(inset.start_date).add(2, 'days').format('YYYY-MM-DD') : undefined}
                          onChange={e => handleInsetChange(idx, 'end_date', e.target.value)}
                          disabled={!inset.start_date || isHolidayDataLoading || insetRemovingById[inset.id] === true}
                          aria-invalid={Boolean(insetErrors[inset.id])}
                          aria-describedby={insetErrors[inset.id] ? `inset-${inset.id}-error` : undefined}
                          className={`block mt-1 w-full min-w-0 border px-2 py-1 text-sm rounded h-9 ${insetErrors[inset.id] ? 'border-red-500' : 'border-sas'}`}
                        />
                      </label>
                    </div>
                  )}
                  </div>
                  {isCoverAndCareSchool && (
                    <p className="mt-3 text-xs text-gray-600">Choose 1–3 days, including the start date. Use the same start and end date for one day.</p>
                  )}
                  {insetErrors[inset.id] && (
                    <p id={`inset-${inset.id}-error`} role="alert" className="mt-1 text-sm text-red-600">
                      {insetErrors[inset.id]}
                    </p>
                  )}
                  {insetDirty[inset.id] && <p className="mt-2 text-sm text-red-700">Unsaved changes</p>}
                </div>
              ))}
              {insetDays.length > 0 && (
                <button
                  type="button"
                  onClick={saveInsets}
                  disabled={!hasUnsavedInsets || isHolidayDataLoading || Object.values(insetRemovingById).some(Boolean)}
                  className="mt-2 rounded bg-sas px-4 py-2 text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingInset ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>

          </div>

          <div className="flex flex-wrap items-center justify-start gap-3 py-2 my-4">
            <button
              onClick={submitHolidays}
              disabled={isHolidayDataLoading || hasHolidayErrors || hasInsetErrors || hasUnsavedInsets}
              className="inline-flex items-center border border-white text-sas bg-white rounded-md overflow-hidden text-sm font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="px-4 py-2 inline-flex items-center gap-2">
                {isSubmittingHolidays ? (
                  <>
                    Loading...
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </>
                ) : (
                  'Submit your Holidays'
                )}
              </span>
              <span className="bg-sas text-white px-3 py-2">
                <FontAwesomeIcon icon={faPlus} />
              </span>
            </button>
            {isDraft && (
              <button onClick={resetHolidayStore} disabled={isFetchingSchools || schools.length === 0 || isHolidayDataLoading} className={`inline-flex items-center border shadow-md hover:shadow-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed ${activeView === 'form' ? 'border-orange-600 text-orange-600' : 'border-white text-white'
                } rounded-md overflow-hidden text-sm font-medium`}>
                <span className="px-4 py-2">Revert All Changes</span>
                <span className="bg-orange-600 text-white px-3 py-2">
                  <FontAwesomeIcon icon={faArrowRotateLeft} />
                </span>
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Holidays
